// SUB-OFFICE 7 — print-layer chrome shared by compose, verdict, and the
// scripted ceremonies: one injected stylesheet (letterpress government print),
// one overlay root, the HOLD-TO-SLAM verb, stamps, and [[R]] redaction bars.
// Reduced motion: slams become fades; the shake is off; nothing else changes.

let styled = false;
let root: HTMLElement | null = null;

export function printLayer(): HTMLElement {
  if (!styled) {
    styled = true;
    const s = document.createElement('style');
    s.textContent = CSS_TEXT;
    document.head.appendChild(s);
  }
  if (!root) {
    root = document.createElement('div');
    root.id = 'print-layer';
    document.body.appendChild(root);
  }
  return root;
}

export const REDUCED = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Render text with [[R]]…[[/R]] spans as redaction bars (contract §7). */
export function redacted(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const parts = text.split(/\[\[R\]\]|\[\[\/R\]\]/);
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 1) {
      const bar = document.createElement('span');
      bar.className = 'redact';
      bar.textContent = '█'.repeat(Math.max(3, Math.min(14, parts[i].length)));
      bar.title = 'CONTENT WITHHELD FOR YOUR PROTECTION.';
      frag.appendChild(bar);
    } else if (parts[i]) {
      frag.appendChild(document.createTextNode(parts[i]));
    }
  }
  return frag;
}

/** Memo text with the petitioner's exact quoted phrases underlined in ink. */
export function memoWithQuotes(memo: string, quotes: string[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  let rest = memo;
  const spans: { at: number; len: number }[] = [];
  for (const q of quotes || []) {
    if (!q) continue;
    const at = rest.indexOf(q);
    if (at >= 0) spans.push({ at, len: q.length });
  }
  spans.sort((a, b) => a.at - b.at);
  let cursor = 0;
  for (const sp of spans) {
    if (sp.at < cursor) continue;
    if (sp.at > cursor) frag.appendChild(document.createTextNode(rest.slice(cursor, sp.at)));
    const em = document.createElement('span');
    em.className = 'quoted';
    em.textContent = rest.slice(sp.at, sp.at + sp.len);
    frag.appendChild(em);
    cursor = sp.at + sp.len;
  }
  if (cursor < rest.length) frag.appendChild(document.createTextNode(rest.slice(cursor)));
  return frag;
}

// ---------------------------------------------------------------------------
// HOLD-TO-SLAM — pointerdown builds a 400ms rise; release slams (3deg + thunk).
// Release under 150ms is a weak thunk, never a fail. Holding 1.5s past the
// rise requests formality (the plunger's opt-in reverence).
// ---------------------------------------------------------------------------

export interface SlamResult {
  heldMs: number;
  weak: boolean;
  formality: boolean;
}

export function holdSlamButton(
  label: string,
  onSlam: (r: SlamResult) => void,
  opts: { formalityHint?: boolean; className?: string } = {}
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'slam-btn letterhead' + (opts.className ? ' ' + opts.className : '');
  btn.textContent = label;
  btn.style.touchAction = 'none';
  let downAt = 0;
  let raf = 0;
  let fired = false;

  const rise = (): void => {
    if (!downAt) return;
    const k = Math.min(1, (performance.now() - downAt) / 400);
    btn.style.transform = `translateY(${(-6 * k).toFixed(1)}px) scale(${(1 + 0.05 * k).toFixed(3)})`;
    if (opts.formalityHint && performance.now() - downAt >= 1900) btn.classList.add('formality');
    raf = requestAnimationFrame(rise);
  };

  const release = (): void => {
    if (!downAt || fired) return;
    fired = true;
    cancelAnimationFrame(raf);
    const heldMs = performance.now() - downAt;
    downAt = 0;
    const weak = heldMs < 150;
    btn.classList.remove('formality');
    if (REDUCED()) {
      btn.style.transform = '';
      btn.classList.add('slammed-fade');
    } else {
      btn.style.transform = `translateY(1px) rotate(${weak ? 1 : 3}deg)`;
      shake(weak ? 1 : 2);
      window.setTimeout(() => {
        btn.style.transform = '';
      }, 130);
    }
    onSlam({ heldMs, weak, formality: opts.formalityHint === true && heldMs >= 1900 });
    window.setTimeout(() => {
      fired = false;
    }, 250);
  };

  btn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    downAt = performance.now();
    fired = false;
    rise();
  });
  btn.addEventListener('pointerup', release);
  btn.addEventListener('pointercancel', () => {
    cancelAnimationFrame(raf);
    downAt = 0;
    btn.style.transform = '';
  });
  btn.addEventListener('keydown', (e) => {
    if ((e.code === 'Enter' || e.code === 'Space') && !downAt) {
      e.preventDefault();
      downAt = performance.now();
      fired = false;
      rise();
    }
  });
  btn.addEventListener('keyup', (e) => {
    if (e.code === 'Enter' || e.code === 'Space') release();
  });
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
  return btn;
}

/** 2px decaying screenshake on the print layer (reduced motion: none). */
export function shake(px: number): void {
  if (REDUCED()) return;
  const el = printLayer();
  el.style.transition = 'none';
  el.style.transform = `translate(${px}px, ${-px}px)`;
  window.setTimeout(() => {
    el.style.transform = `translate(${-px}px, ${px * 0.6}px)`;
    window.setTimeout(() => {
      el.style.transform = '';
    }, 40);
  }, 40);
}

/** A rubber stamp element (rotated, ink-missed). */
export function stampEl(text: string, tone: 'ink' | 'red' | 'blue' | 'gold'): HTMLElement {
  const el = document.createElement('div');
  el.className = `stamp stamp-${tone} letterhead`;
  el.textContent = text;
  return el;
}

/** Slam a stamp into a container: 3-frame anticipation, then all at once. */
export function slamStamp(
  container: HTMLElement,
  stamp: HTMLElement,
  onImpact?: () => void
): void {
  container.appendChild(stamp);
  if (REDUCED()) {
    stamp.classList.add('stamp-fade-in');
    if (onImpact) onImpact();
    return;
  }
  stamp.classList.add('stamp-pre');
  window.setTimeout(() => {
    stamp.classList.remove('stamp-pre');
    stamp.classList.add('stamp-hit');
    shake(2);
    if (onImpact) onImpact();
  }, 150);
}

// ---------------------------------------------------------------------------

const CSS_TEXT = `
#print-layer{position:fixed;inset:0;z-index:24;pointer-events:none}
#print-layer>*{pointer-events:auto}
.paper-modal{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  background:var(--paper);color:var(--carbon);border:1px solid var(--carbon);
  box-shadow:0 14px 40px rgba(28,26,23,.5), 2px 2px 0 rgba(28,26,23,.35);
  max-height:92vh;overflow-y:auto;overflow-x:hidden}
.paper-modal.wide{width:min(94vw,620px)}
.paper-modal.slim{width:min(94vw,430px)}
.paper-head{border-bottom:1px solid var(--carbon);padding:10px 18px 8px;
  font-size:11px;letter-spacing:.14em;display:flex;justify-content:space-between;gap:12px}
.paper-body{padding:14px 18px 16px;font-size:13px;line-height:1.65}
.paper-foot{border-top:1px solid var(--carbon);padding:10px 18px 12px;
  display:flex;gap:10px;justify-content:flex-end;align-items:center;flex-wrap:wrap}
.rule{border:none;border-top:1px solid var(--carbon);margin:10px 0}
.field-label{font-size:10px;letter-spacing:.14em;text-transform:uppercase;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif;opacity:.75;margin-bottom:2px}
.typeline{font-family:ui-monospace,'Courier New',monospace}
.redact{background:var(--carbon);color:var(--carbon);letter-spacing:-1px;padding:0 2px}
.quoted{border-bottom:2px solid rgba(28,26,23,.75);font-style:normal}
.tab-num{font-variant-numeric:tabular-nums}

/* hold-to-slam */
.slam-btn{appearance:none;background:var(--carbon);color:var(--paper);border:none;
  padding:12px 22px 11px;font-size:12px;letter-spacing:.14em;text-transform:uppercase;
  cursor:pointer;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;
  box-shadow:0 5px 0 rgba(28,26,23,.45);min-height:44px}
.slam-btn:focus-visible{outline:2px solid var(--amber);outline-offset:2px}
.slam-btn.secondary{background:var(--paper);color:var(--carbon);border:1px solid var(--carbon);
  box-shadow:0 4px 0 rgba(28,26,23,.25)}
.slam-btn.red{background:var(--stamp-red)}
.slam-btn.formality{box-shadow:0 5px 0 var(--amber)}
.slam-btn.slammed-fade{opacity:.7}

/* stamps */
.stamp{display:inline-block;padding:6px 14px;border:3px solid currentColor;
  font-weight:700;font-size:17px;letter-spacing:.1em;transform:rotate(-3deg);
  -webkit-mask-image:radial-gradient(ellipse at 38% 44%, #000 62%, rgba(0,0,0,.72) 78%, rgba(0,0,0,.5) 100%);
  mask-image:radial-gradient(ellipse at 38% 44%, #000 62%, rgba(0,0,0,.72) 78%, rgba(0,0,0,.5) 100%)}
.stamp-ink{color:var(--carbon)} .stamp-red{color:var(--stamp-red)}
.stamp-blue{color:var(--clemency)} .stamp-gold{color:var(--gold)}
.stamp-pre{opacity:0;transform:rotate(-3deg) translateY(-26px) scale(1.3)}
.stamp-hit{opacity:1;transform:rotate(-3deg);transition:none}
.stamp-fade-in{animation:stampFade .4s linear}
@keyframes stampFade{from{opacity:0}to{opacity:1}}

/* compose sheet */
.compose-dim{position:absolute;inset:0;background:rgba(28,26,23,.38)}
.compose-sheet{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  width:min(94vw,600px);max-height:92vh;overflow-y:auto;background:var(--paper);
  color:var(--carbon);border:1px solid var(--carbon);
  box-shadow:0 18px 50px rgba(28,26,23,.55);
  background-image:radial-gradient(circle at 14px 50%, transparent 0, transparent 100%);
  padding:0 0 4px}
.tractor{height:14px;background-image:radial-gradient(circle, rgba(28,26,23,.35) 2.5px, transparent 3px);
  background-size:28px 14px;background-position:8px center;border-bottom:1px dashed rgba(28,26,23,.25)}
.tractor.bottom{border-bottom:none;border-top:1px dashed rgba(28,26,23,.25)}
.compose-demand{padding:12px 20px 8px;font-size:13px;line-height:1.6;white-space:pre-wrap}
.compose-demand .formid{font-size:11px;letter-spacing:.14em;margin-bottom:6px;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif;text-transform:uppercase}
.compose-area{display:block;width:calc(100% - 40px);margin:4px 20px 8px;min-height:130px;
  background:transparent;border:none;border-top:1px solid rgba(28,26,23,.4);resize:vertical;
  font-family:ui-monospace,'Courier New',monospace;font-size:15px;line-height:26px;color:var(--carbon);
  background-image:repeating-linear-gradient(transparent 0 25px, rgba(28,26,23,.14) 25px 26px);
  padding-top:6px;outline:none;-webkit-user-select:text;user-select:text}
.checklist{margin:0 20px 8px;font-size:11.5px;font-family:ui-monospace,'Courier New',monospace}
.checklist .row{display:flex;gap:8px;align-items:baseline;padding:1px 0}
.checklist .mark{width:16px;display:inline-block;text-align:center}
.checklist .pass .mark{color:var(--carbon);animation:penscratch .18s steps(2,end)}
.checklist .fail{opacity:.6}
.checklist .fail .mark{color:var(--stamp-red)}
@keyframes penscratch{0%{transform:scale(.3) rotate(-20deg)}100%{transform:scale(1) rotate(0)}}
.compose-foot{display:flex;justify-content:space-between;align-items:center;gap:10px;
  padding:8px 20px 10px;border-top:1px solid var(--carbon)}
.compose-note{font-size:10.5px;opacity:.7;letter-spacing:.06em;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif;text-transform:uppercase}
.blurred-ink{filter:blur(2px)}
.hold-track{height:10px;border:1px solid var(--carbon);margin:12px 20px;position:relative}
.hold-fill{position:absolute;inset:1px auto 1px 1px;width:0%;background:var(--carbon)}
.sorting-grid{display:flex;flex-wrap:wrap;gap:8px;margin:10px 20px}
.sorting-grid button{font-family:ui-monospace,'Courier New',monospace;font-size:14px;
  padding:10px 12px;border:1px solid var(--carbon);background:var(--paper);cursor:pointer;min-width:64px}
.sorting-grid button.sorted{background:var(--carbon);color:var(--paper)}
.triplicate-banner{margin:0 20px 8px;padding:6px 10px;border:2px solid var(--stamp-red);
  color:var(--stamp-red);font-size:11px;letter-spacing:.12em;text-transform:uppercase;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif}

/* verdict slip */
.slip{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  width:min(94vw,460px);max-height:92vh;overflow-y:auto;background:var(--paper);color:var(--carbon);
  border:1px solid var(--carbon);box-shadow:0 16px 44px rgba(28,26,23,.6);
  font-family:ui-monospace,'Courier New',monospace;font-size:13px;line-height:1.6;
  padding:14px 20px 16px}
.slip .line{white-space:pre-wrap;min-height:1.2em;opacity:0}
.slip .line.printed{opacity:1}
.slip .line.rule-line{border-top:1px solid rgba(28,26,23,.55);margin:7px 0 6px;min-height:0}
.slip .rubric{display:flex;justify-content:space-between}
.slip .glyph-jitter span{display:inline-block}
.slip-stamp-zone{min-height:56px;display:flex;align-items:center;justify-content:center;margin:8px 0 4px}
.slip-actions{display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-top:10px}
.slip .memo-block{margin:4px 0}
.chit{width:min(92vw,340px)}
.chit .paper-body{font-size:12px}

/* appeals: the one wood-paneled room */
.appeals-veneer{position:absolute;inset:0;background:
  radial-gradient(ellipse at 50% 30%, rgba(255,214,140,.20), transparent 55%),
  linear-gradient(rgba(46,30,17,.93), rgba(36,24,14,.96));}
.appeals-card{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  width:min(94vw,540px);max-height:92vh;overflow-y:auto;background:#EFE6D2;color:#2A1D10;
  border:1px solid #2A1D10;box-shadow:0 0 0 8px #4A3320, 0 20px 50px rgba(0,0,0,.7);
  padding:16px 22px 18px;font-family:ui-monospace,'Courier New',monospace;font-size:13px;line-height:1.65}
.appeals-card textarea{width:100%;min-height:110px;background:transparent;border:none;
  border-top:1px solid rgba(42,29,16,.5);outline:none;resize:vertical;
  font-family:ui-monospace,'Courier New',monospace;font-size:14px;line-height:24px;color:#2A1D10;
  background-image:repeating-linear-gradient(transparent 0 23px, rgba(42,29,16,.15) 23px 24px)}
.ellipsis-wait{letter-spacing:.4em;animation:sigh 1.6s steps(4,end) infinite}
@keyframes sigh{0%{opacity:.3}50%{opacity:.9}100%{opacity:.3}}

/* docket strip */
#docket-strip{position:fixed;left:14px;top:14px;z-index:21;pointer-events:none;
  font-family:ui-monospace,'Courier New',monospace;font-size:11px;color:var(--bone);
  background:rgba(28,26,23,.82);border:1px solid rgba(183,179,168,.5);padding:7px 10px 8px;
  letter-spacing:.06em;min-width:150px}
#docket-strip .ds-head{font-size:9.5px;letter-spacing:.16em;opacity:.8;margin-bottom:4px;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif}
#docket-strip .ds-row{display:flex;gap:7px;align-items:baseline;white-space:nowrap}
#docket-strip .ds-row .box{width:12px;display:inline-block}
#docket-strip .ds-row.done{opacity:.5;text-decoration:line-through}
#docket-strip .ds-row.active .box{color:var(--amber)}
#docket-strip .ds-cleared{margin-top:5px;color:var(--amber);letter-spacing:.14em}
#docket-strip .ds-banner{margin-top:5px;color:var(--stamp-red);letter-spacing:.1em}

/* ticket + certificate ceremonies */
.ticket-card{position:absolute;left:50%;top:44%;width:min(88vw,300px);
  transform:translate(-50%,-50%) rotate(-1.5deg);background:var(--paper);color:var(--carbon);
  border:1px solid var(--carbon);box-shadow:0 12px 34px rgba(28,26,23,.6);
  font-family:ui-monospace,'Courier New',monospace;text-align:center;padding:14px 16px 18px}
.ticket-card .tick-no{font-size:34px;letter-spacing:.06em;margin:6px 0}
.ticket-drop{animation:tickdrop .55s cubic-bezier(.2,.9,.3,1.15)}
@keyframes tickdrop{0%{transform:translate(-50%,-160%) rotate(4deg)}70%{transform:translate(-50%,-46%) rotate(-2.4deg)}100%{transform:translate(-50%,-50%) rotate(-1.5deg)}}
.cert-code{border:1px dashed var(--carbon);padding:10px 12px;margin:10px 0;
  font-size:16px;letter-spacing:.1em;text-align:center}
.gold-rule{border:none;border-top:2px solid var(--gold);margin:10px 0}

/* review letter / personnel action memo */
.letterhead-doc .lh-title{font-size:14px;letter-spacing:.18em;text-transform:uppercase;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif;font-weight:700}
.letterhead-doc .lh-sub{font-size:10px;letter-spacing:.14em;text-transform:uppercase;opacity:.7;
  font-family:'Helvetica Neue',Helvetica,Arial,ui-sans-serif,sans-serif}

@media (prefers-reduced-motion:reduce){
  .ticket-drop{animation:none}
  .checklist .pass .mark{animation:none}
  .ellipsis-wait{animation:none;opacity:.7}
}
`;
